import { ProfileRecommendation } from 'containers/recommendations/types/recommendations'
import { User } from 'containers/user/types/user'
import { filterObjectsByExistingKeys } from 'utilities/filterObjects'

export const DEFAULT_CONNECTIONS_QUERY_PARAMS = {
  page: 0,
  searchQuery: '',
}

export type PaginationItem = {
  page: number | '...'
  lastPage?: number
}

/**
 * Creates a pagination array based on the current page, total number of pages, and limit.
 * @param currentPage The current page number.
 * @param pages The total number of pages.
 * @param limit The maximum number of pages to display in the pagination.
 * @returns An array of PaginationItem objects representing the pagination.
 */
export const createPagination = (
  currentPage: number,
  pages: number,
  limit: number,
  onlyPages?: boolean
): PaginationItem[] => {
  const pagination: PaginationItem[] = []

  // Calculate the start and end page numbers for the pagination
  let start = Math.max(1, currentPage - Math.floor(limit / 2))
  let end = Math.min(pages, currentPage + Math.floor(limit / 2))

  // Adjust the start and end page numbers if they are too close to the edges
  if (currentPage - start < Math.floor(limit / 2)) {
    end = Math.min(pages, end + (Math.floor(limit / 2) - (currentPage - start)))
  }
  if (end - currentPage < Math.floor(limit / 2)) {
    start = Math.max(1, start - (Math.floor(limit / 2) - (end - currentPage)))
  }

  // Add ellipsis and first page if necessary
  if (start > 1 && !onlyPages) {
    pagination.push({ page: 1 })
    if (start > 2) {
      pagination.push({ page: '...', lastPage: start - 1 })
    }
  }

  // Add the page numbers to the pagination array
  for (let i = start; i <= end; i++) {
    pagination.push({ page: i })
  }

  // Add ellipsis and last page if necessary
  if (end < pages && !onlyPages) {
    if (end < pages - 1) {
      pagination.push({ page: '...', lastPage: end + 1 })
    }
    pagination.push({ page: pages })
  }

  return pagination
}

export const cleanUsersByRepeatedId = (
  users: User[] | ProfileRecommendation[]
) => {
  const usersMap = new Map()
  users.forEach((user) => {
    if (!usersMap.has(user.id)) {
      usersMap.set(user.id, user)
    }
  })
  return Array.from(usersMap.values())
}

export const cleanProfilesWithoutId = (
  connections: User[] | ProfileRecommendation[]
) => cleanUsersByRepeatedId(filterObjectsByExistingKeys(connections, ['id']))
